// Helper murni — definisi Role Admin & Perijinan Admin. Tanpa Supabase.
//
// SEJAK 2026-08 (mig 3780) perizinan admin GRANULAR: satu kunci = satu
// kemampuan spesifik ("konfirmasi pembayaran", "edit stok", "login sebagai
// pelanggan", …), dikelompokkan per domain untuk UI mobile-first di
// /admin/settings/admin-management. Tujuh kunci MODUL lama (pesanan, stok,
// produk, pelanggan, keuangan, admin, pengaturan) TETAP SAH sebagai "payung"
// back-compat: admin yang kolom `admin_permissions`-nya masih berisi kunci
// modul otomatis memegang semua kunci granular ber-`legacyParent` modul itu.
// Tanpa payung ini, klien lama yang menyimpan daftar modul (POST
// /api/admin/manage versi lama) akan MENCABUT izin granular secara senyap.
//
// Kunci granular ber-`legacyParent: null` = SENSITIF: TIDAK pernah diwarisi
// dari kunci modul — harus dicentang eksplisit (super admin selalu punya).
// `grandfathered: true` = kemampuan yang HARI INI terbuka untuk semua admin
// (guard SQL-nya dulu hanya `is_admin()`); migrasi 3780 mem-backfill kunci ini
// ke semua admin existing supaya rollout TIDAK mengubah perilaku — pemilik
// lalu MENCABUT per admin dari UI. Kunci sensitif yang dulu Super-Admin-only
// (nilai aset stok, saldo bank, backup, reset) SENGAJA tidak di-backfill.
//
// Kembaran SQL: mig 0711 (kolom + RPC) + mig 3780 (granular):
//   • admin_permission_legacy_parent(perm) — CERMIN `LEGACY_PARENT` di bawah.
//   • admin_has_permission(perm) — super admin → true; keanggotaan langsung;
//     atau kunci payung legacy-nya ada di kolom.
// Paritas dikunci `adminPermissions.test.ts` ↔
// `supabase/tests/admin_granular_permissions_3780.sql` (daftar kasus sama).
//
// `label` juga punya kembaran SQL: pesan "Butuh izin "…"" yang dilempar RPC
// (`admin_assert_permission(kunci, label)` + kalimat langsung) menyebut label
// ini supaya Super Admin menemukan centangnya di Perizinan Admin. Mengganti
// label = migrasi baru yang menambal label SQL-nya + blok `$katalog_izin$`
// baru (pola mig 8290); paritasnya dikunci `adminPermissionLabelParity.test.ts`.

export type AdminRoleId = "super_admin" | "admin_biaya" | "admin_umum";

/** Kunci MODUL lama (payung back-compat). Jangan tambah anggota baru di sini. */
export type AdminLegacyPermissionKey =
  | "pesanan"
  | "stok"
  | "produk"
  | "pelanggan"
  | "keuangan"
  | "admin"
  | "pengaturan";

/** Kunci izin GRANULAR — satu kunci = satu kemampuan. */
export type AdminGrantKey =
  // Pesanan & Pengiriman
  | "pesanan.kelola"
  | "pesanan.manual-produksi"
  | "pesanan.edit-produksi"
  | "pesanan.ubah-harga"
  | "pesanan.kirim"
  | "pesanan.packing"
  | "pesanan.cetak"
  // Tagihan & Pembayaran
  | "tagihan.kelola"
  | "tagihan.konfirmasi"
  | "tagihan.tandai-lunas"
  | "tagihan.kirim-belum-lunas"
  // Saldo & Poin
  | "saldo.topup"
  | "saldo.kelola"
  | "saldo.merek"
  | "saldo.poin"
  // Stok, Pembelian & Retur
  | "stok.lihat"
  | "stok.kelola"
  | "stok.nilai-aset"
  | "stok.pembelian"
  | "stok.retur"
  // Katalog Produk
  | "produk.kelola"
  | "produk.master"
  | "produk.promo"
  | "produk.voucher"
  | "produk.bundel"
  | "produk.hadiah"
  // Pelanggan & Komunikasi
  | "pelanggan.kelola"
  | "pelanggan.level"
  | "pelanggan.impersonate"
  | "pelanggan.broadcast"
  | "pelanggan.kritik-saran"
  // Kasir (POS)
  | "kasir.akses"
  | "kasir.void"
  | "kasir.ubah-harga"
  // Kasir Marketplace (mig 8550)
  | "marketplace.catat"
  | "marketplace.keuangan"
  // Integrasi Shopee Open Platform (mig 8930)
  | "marketplace.integrasi"
  // Keuangan & Laporan
  | "keuangan.laporan"
  | "keuangan.biaya-produksi"
  | "keuangan.biaya-operasional"
  | "keuangan.saldo-bank-lihat"
  | "keuangan.saldo-bank"
  | "keuangan.kas-tunai-lihat"
  | "keuangan.kas-tunai"
  | "keuangan.biaya-tunai"
  // Pengaturan Toko
  | "pengaturan.website"
  | "pengaturan.pembayaran"
  | "pengaturan.pengiriman"
  | "pengaturan.cetak"
  | "pengaturan.notifikasi"
  // Data & Sistem
  | "data.import-export"
  | "data.backup"
  | "data.reset"
  // Manajemen Admin
  | "admin.kelola";

export type AdminPermissionKey = AdminLegacyPermissionKey | AdminGrantKey;

// ─── Kelompok izin (urutan = urutan tampil di UI) ────────────────────────────

export type AdminPermissionGroupId =
  | "pesanan"
  | "tagihan"
  | "saldo"
  | "stok"
  | "produk"
  | "pelanggan"
  | "kasir"
  | "marketplace"
  | "keuangan"
  | "pengaturan"
  | "data"
  | "admin";

export type AdminPermissionGroupMeta = {
  id: AdminPermissionGroupId;
  label: string;
  description: string;
};

export const ADMIN_PERMISSION_GROUPS: AdminPermissionGroupMeta[] = [
  { id: "pesanan",    label: "Pesanan & Pengiriman",     description: "Board PO, produksi, keranjang reseller & pengiriman" },
  { id: "tagihan",    label: "Tagihan & Pembayaran",     description: "Tagihan, konfirmasi pembayaran, tandai lunas" },
  { id: "saldo",      label: "Saldo & Poin",             description: "Saldo pelanggan, top-up, saldo merek, program poin" },
  { id: "stok",       label: "Stok, Pembelian & Retur",  description: "Kelola stok, opname, pembelian persediaan, retur" },
  { id: "produk",     label: "Katalog Produk",           description: "Produk, merek, kategori, template variasi, promo, voucher, bundling" },
  { id: "pelanggan",  label: "Pelanggan & Komunikasi",   description: "Daftar reseller, level, broadcast, login sebagai pelanggan" },
  { id: "kasir",      label: "Kasir (POS)",              description: "Penjualan di tempat & pembatalan transaksi kasir" },
  { id: "marketplace", label: "Kasir Marketplace",       description: "Scan pesanan Shopee & TikTok Shop, Dana Diterima, toko marketplace" },
  { id: "keuangan",   label: "Keuangan & Laporan",       description: "Laporan, biaya produksi/operasional, saldo bank & kas tunai" },
  { id: "pengaturan", label: "Pengaturan Toko",          description: "Website, rekening, ekspedisi, template cetak, notifikasi" },
  { id: "data",       label: "Data & Sistem",            description: "Import-export, backup & restore, reset data" },
  { id: "admin",      label: "Manajemen Admin",          description: "Daftar admin & assignment handler reseller" },
];

// ─── Katalog izin granular ────────────────────────────────────────────────────

export type AdminGrantMeta = {
  key: AdminGrantKey;
  group: AdminPermissionGroupId;
  label: string;
  description: string;
  /**
   * Kunci MODUL lama yang "memayungi" izin ini (back-compat). `null` =
   * SENSITIF: tak pernah diwarisi dari kunci modul — wajib dicentang eksplisit.
   */
  legacyParent: AdminLegacyPermissionKey | null;
  /**
   * true = kemampuan yang sebelum mig 3780 terbuka utk SEMUA admin (guard SQL
   * hanya `is_admin()`), jadi migrasi mem-backfill-nya ke admin existing agar
   * rollout tidak mengubah perilaku. false = dulu Super-Admin-only → tetap
   * tertutup sampai dicentang.
   */
  grandfathered: boolean;
  /** Tampilkan badge peringatan (aksi uang/nasib data/identitas). */
  danger?: boolean;
  /** Catatan "dulu khusus Super Admin" utk ditampilkan di UI. */
  superOnlyBefore?: boolean;
  /**
   * Kunci yang IKUT tercentang saat kunci ini dicentang (mig 8620: "Kelola"
   * mencakup "Lihat", "semua rekening" mencakup "Tunai"). Hanya urusan UI
   * Perizinan + `effectivePermissions`; guard SQL-nya sendiri sudah
   * memperlakukan kunci atas sebagai pemegang kunci bawah (`admin_bank_scope`).
   */
  implies?: AdminGrantKey[];
};

export const ADMIN_GRANTS: AdminGrantMeta[] = [
  // ── Pesanan & Pengiriman ──
  {
    key: "pesanan.kelola", group: "pesanan", legacyParent: "pesanan", grandfathered: true,
    label: "Kelola pesanan & keranjang reseller",
    description: "Board Pre Order & produksi, keranjang admin: tambah/edit/batalkan pesanan, tandai ready, gagal produksi, tutup/buka PO. Tambah & edit pesanan di Detail Produksi diatur izin tersendiri.",
  },
  {
    // Mig 6410 — dipisah dari `pesanan.kelola` atas permintaan pemilik
    // (2026-09-28): pesanan susulan saat produk sudah di meja produksi hanya
    // boleh diinput admin yang ditunjuk. Polanya `pelanggan.impersonate`:
    //  • `legacyParent: null` — tak diwarisi kunci modul `pesanan` dan tak ikut
    //    preset role mana pun, jadi admin BARU wajib dicentang eksplisit;
    //  • `grandfathered: false` — bendera itu milik backfill mig 3780 (dipatok
    //    32 kunci). Admin non-super yang SAAT mig 6410 diterapkan memegang
    //    `pesanan.kelola` di-backfill oleh migrasi itu sendiri, jadi rollout tak
    //    mengubah perilaku; pemilik lalu MENCABUT per admin dari UI.
    // Guard server: `admin_create_production_order` (+ pembungkus over-quota
    // yang mendelegasikan ke sana) — satu-satunya pintu input pesanan untuk
    // produk PROSES PRODUKSI; pintu lain menolak status itu.
    key: "pesanan.manual-produksi", group: "pesanan", legacyParent: null, grandfathered: false,
    label: "Tambah pesanan manual di Detail Produksi",
    description: "Tombol “Tambah Pesanan Manual” di Detail Produksi — input pesanan susulan pelanggan saat produk berstatus Proses Produksi.",
  },
  {
    // Mig 7320 — tombol Edit kartu Rekap Per Pelanggan Detail Produksi
    // (permintaan pemilik 2026-10-15). Pola `pesanan.manual-produksi`, TAPI
    // TANPA backfill (putusan pemilik): bawaannya hanya Super Admin.
    //  • `legacyParent: null` — tak diwarisi kunci modul `pesanan`, tak ikut
    //    preset role → admin mana pun wajib dicentang eksplisit;
    //  • `grandfathered: false` — bendera itu milik backfill mig 3780.
    // Guard server: `admin_edit_production_order` + pembungkus over-quota-nya.
    // Menulis ulang baris pesanan & harganya → `danger`.
    key: "pesanan.edit-produksi", group: "pesanan", legacyParent: null, grandfathered: false, danger: true,
    label: "Edit pesanan di Detail Produksi",
    description: "Tombol “Edit” di kartu Rekap Per Pelanggan Detail Produksi — ubah varian, qty, dan catatan pesanan pelanggan saat produk berstatus Proses Produksi (sebelum Mulai Dikirim).",
  },
  {
    // Mig 7060 — dipisah dari `pesanan.kelola` atas putusan pemilik
    // (2026-10-08, Q4): Ubah Harga / Ubah Diskon baris pesanan di keranjang
    // admin menggeser uang yang ditagih. Pola `pesanan.manual-produksi`:
    //  • `legacyParent: null` — tak diwarisi kunci modul `pesanan`, tak ikut
    //    preset role → admin BARU wajib dicentang eksplisit;
    //  • `grandfathered: false` — admin non-super pemegang `pesanan.kelola`
    //    SAAT mig 7060 diterapkan di-backfill migrasi itu sendiri, jadi rollout
    //    tak mengubah siapa yang bisa; pemilik lalu MENCABUT per admin.
    // Guard server: `admin_set_order_items_price`. Ubah Nama produk Non Web
    // TETAP ikut `pesanan.kelola` (bukan uang).
    //
    // Mig 8440 (bughunt G8-21, putusan pemilik 2026-10-22): admin TANPA kunci
    // ini juga digerbang di 8 RPC penulis pesanan (`update_open_preorder_order_items`,
    // `admin_edit_closed_po_order`, `admin_add_order_lines`, `admin_create_order`,
    // `admin_create_closed_po_order`, `admin_create_production_order`,
    // `admin_edit_production_order`, `admin_migrate_web_order`; pembungkus
    // `_over_quota` & Gabung Pesanan ikut) dan di PATCH/POST langsung
    // `order_items` (pemicu `trg_order_items_price_guard` — kolom harga DAN
    // kolom identitas order_id/product_id/variant_id/variant_name/
    // order_bundle_id) serta PATCH `orders.customer_id` (pemicu
    // `trg_orders_customer_price_guard`). Bentuknya RENTANG,
    // bukan kesamaan persis: harga baru wajib di antara lantai level pelanggan
    // (aturan merek / override diskon varian slot level / diskon rata) dan
    // harga normal + tambahan — `admin_assert_order_line_price`, cermin tangga
    // pricing.ts (paritas: orderLinePriceRangeParity.test.ts). Harga tersimpan
    // varian yang sama tetap boleh dipertahankan. Sisa yang BELUM dijaga:
    // produk promo (lantai minggir, plafon tetap), Non Web (tetap
    // `pesanan.kelola`), checkout pembeli (A2-1), kesamaan persis dengan harga
    // mesin (A2-1 Tahap 1). Deskripsi di bawah menyebut sisa promo & Non Web.
    // Rentang dibaca dari katalog hidup: admin yang juga memegang
    // `produk.promo`/`produk.kelola`/`pelanggan.level` bisa menggeser rentangnya
    // sendiri — bagi admin seperti itu gerbang ini jejak, bukan kunci.
    key: "pesanan.ubah-harga", group: "pesanan", legacyParent: null, grandfathered: false, danger: true,
    label: "Ubah harga & diskon pesanan",
    description: "Tombol “Ubah Harga” dan “Ubah Diskon” per baris di keranjang admin. Tanpa izin ini, harga pesanan yang ditambah atau diedit dijaga di antara harga level pelanggan dan harga normal. Produk yang sedang promo hanya dijaga batas atasnya. Produk Non Web tidak dijaga.",
  },
  {
    key: "pesanan.kirim", group: "pesanan", legacyParent: "pesanan", grandfathered: true,
    label: "Kelola pengiriman",
    // Bughunt G8-30 (putusan pemilik 2026-10-22): Gabungkan Kiriman memindah
    // ongkir yang sudah dibayar ke nota ongkir paket gabungan tanpa izin "Catat
    // pembayaran & batalkan pelunasan" — Super Admin perlu tahu saat mencentang.
    description: "Request kirim, set ongkir, input resi, revisi isi paket, batalkan pengiriman, tandai diterima. Gabungkan kiriman ikut memindah ongkir yang sudah dibayar ke tagihan ongkir paket gabungan.",
  },
  {
    // Mig 7290 — Scan Produk untuk di Packing, dipisah dari `pesanan.kirim`
    // atas putusan pemilik (D4, 2026-10-14): staf gudang boleh men-scan isi
    // paket tanpa hak set ongkir / input resi / batalkan kiriman. Kunci
    // NON-sensitif → berpayung modul lama `pesanan` (preseden `produk.voucher`
    // mig 5610): pemegang kunci modul & preset role ber-`pesanan` otomatis
    // memilikinya, sedangkan pemegang GRANULAR `pesanan.kirim` di-backfill
    // migrasinya. `grandfathered: false` — bendera itu milik backfill mig 3780.
    // Guard server: `admin_push_packing_scans` + `admin_reset_packing_scans`.
    key: "pesanan.packing", group: "pesanan", legacyParent: "pesanan", grandfathered: false,
    label: "Scan packing pengiriman",
    description: "Scan SKU tiap pcs yang masuk paket (kamera HP / alat scanner), centang manual barang tanpa SKU, batalkan & reset scan. Paket yang lengkap otomatis ditandai Siap Kirim.",
  },
  {
    key: "pesanan.cetak", group: "pesanan", legacyParent: "pesanan", grandfathered: true,
    label: "Cetak dokumen pesanan",
    description: "Label alamat, surat jalan / packing list, dan label barcode (produksi & manual).",
  },

  // ── Tagihan & Pembayaran ──
  {
    key: "tagihan.kelola", group: "tagihan", legacyParent: "keuangan", grandfathered: true,
    label: "Kelola tagihan",
    description: "Buat tagihan, ACC/tolak pengajuan tagihan, edit tagihan, void, diskon manual, tagih via WhatsApp.",
  },
  {
    key: "tagihan.konfirmasi", group: "tagihan", legacyParent: "keuangan", grandfathered: true,
    label: "Konfirmasi pembayaran",
    description: "Terima / tolak bukti transfer tagihan — setoran tunggal, batch “Bayar Sekaligus”, dan kombinasi.",
  },
  {
    key: "tagihan.tandai-lunas", group: "tagihan", legacyParent: "keuangan", grandfathered: true, danger: true,
    label: "Catat pembayaran & batalkan pelunasan",
    description: "Catat pembayaran yang sudah diterima (transfer, tunai, potong saldo, atau kombinasi) tanpa menunggu bukti pembeli, dan batalkan pelunasan (refund ke saldo).",
  },
  {
    // Mig 8900 — pertanyaan pemilik 2026-10-29 ("apakah ada izinnya di
    // pengaturan?" untuk "Langsung buat pengiriman SEKARANG"): memberangkatkan
    // barang sebelum tagihannya lunas kini izin tersendiri. Pola
    // `pesanan.manual-produksi` (mig 6410):
    //  • `legacyParent: null` — tak diwarisi kunci modul `keuangan`/`pesanan`,
    //    tak ikut preset role → admin BARU wajib dicentang eksplisit;
    //  • `grandfathered: false` — bendera itu milik backfill mig 3780. Admin
    //    non-super yang SAAT mig 8900 diterapkan bisa mencapai salah satu
    //    centangnya (efektif `tagihan.kelola`, `pesanan.kelola` atau
    //    `pesanan.kirim`) di-backfill migrasi itu sendiri, jadi rollout tak
    //    mengubah siapa yang bisa; pemilik lalu MENCABUT per admin.
    // Guard server, bersyarat bendera (jalur lunas tak tersentuh):
    // `admin_create_invoice_with_shipment` (`p_ship_now`; `_idem`/`_voucher`
    // mendelegasikan), `create_shipment_request` (`p_allow_unpaid`; `_idem`,
    // `_with_voucher`, `create_self_shipment_request` mendelegasikan),
    // `admin_revise_shipment_items` (`p_allow_unpaid`), dan — tanpa syarat —
    // `admin_kirim_tagihan` (Kirim Barang dari tagihan belum lunas, mig 8910).
    // Kirim duluan sebelum tagihan ONGKIR dibayar
    // (`admin_release_unpaid_shipment`) di luar kunci ini — barangnya sendiri
    // sudah lunas.
    key: "tagihan.kirim-belum-lunas", group: "tagihan", legacyParent: null, grandfathered: false, danger: true,
    label: "Kirim barang belum lunas",
    description: "Centang “Langsung buat pengiriman sekarang” di Tagihan + Kirim, “Izinkan kirim pcs yang belum lunas” di Request Kirim, tombol Kirim Barang di tagihan yang belum lunas, dan menambah pcs belum lunas lewat Revisi Isi Paket. Barang berangkat sebelum dibayar, tagihannya jadi piutang.",
  },

  // ── Saldo & Poin ──
  {
    key: "saldo.topup", group: "saldo", legacyParent: "keuangan", grandfathered: true,
    label: "Verifikasi top-up saldo",
    description: "Setujui / tolak permintaan top-up saldo pelanggan.",
  },
  {
    key: "saldo.kelola", group: "saldo", legacyParent: "keuangan", grandfathered: true, danger: true,
    label: "Tambah & kurangi saldo pelanggan",
    description: "Penyesuaian saldo manual di Kelola Saldo + import Excel Saldo Pelanggan.",
  },
  {
    key: "saldo.merek", group: "saldo", legacyParent: "keuangan", grandfathered: true,
    label: "Kelola saldo merek",
    description: "Halaman Saldo Merek + penyesuaian saldo merek manual.",
  },
  {
    key: "saldo.poin", group: "saldo", legacyParent: "keuangan", grandfathered: true,
    label: "Kelola poin & program poin",
    description: "Program poin, klaim hadiah, dan kredit/debit poin pelanggan manual.",
  },

  // ── Stok, Pembelian & Retur ──
  {
    key: "stok.lihat", group: "stok", legacyParent: "stok", grandfathered: true,
    label: "Lihat halaman stok",
    description: "Daftar stok, kartu stok, dan tab PO Datang (tanpa nilai uang).",
  },
  {
    key: "stok.kelola", group: "stok", legacyParent: "stok", grandfathered: true,
    label: "Edit stok & opname",
    description: "Tambah/kurangi stok manual, stok opname (di halaman Stok & import Excel), batalkan import opname.",
  },
  {
    key: "stok.nilai-aset", group: "stok", legacyParent: null, grandfathered: false, superOnlyBefore: true,
    label: "Melihat nilai aset di halaman stok",
    description: "Total nilai aset & nilai per varian (modal/HPP) di halaman Stok.",
  },
  {
    key: "stok.pembelian", group: "stok", legacyParent: "stok", grandfathered: true,
    label: "Pembelian stok",
    description: "Catat, ubah & batalkan nota pembelian persediaan (stok naik + kas/bank turun).",
  },
  {
    key: "stok.retur", group: "stok", legacyParent: "stok", grandfathered: true,
    label: "Retur reseller & retur produksi",
    description: "Proses retur barang terkirim (refund ke saldo) dan retur barang ke merek.",
  },

  // ── Katalog Produk ──
  {
    key: "produk.kelola", group: "produk", legacyParent: "produk", grandfathered: true,
    label: "Kelola produk",
    description: "Tambah/edit/duplikat/arsip/hapus produk, form cepat, buka PO lagi.",
  },
  {
    key: "produk.master", group: "produk", legacyParent: "produk", grandfathered: true,
    label: "Kelola master katalog",
    description: "Merek, kategori, segmen, dan template variasi.",
  },
  {
    key: "produk.promo", group: "produk", legacyParent: "produk", grandfathered: true,
    label: "Kelola promo / cuci gudang",
    description: "Kampanye harga coret: buat, ubah, aktif/nonaktifkan.",
  },
  {
    // Fitur baru (mig 5610) → BUKAN grandfathered: tak pernah terbuka untuk
    // semua admin sebelum ini, jadi tak ada perilaku lama yang perlu dijaga.
    // Tetap diwarisi kunci modul "produk" (legacyParent) seperti promo.
    key: "produk.voucher", group: "produk", legacyParent: "produk", grandfathered: false,
    label: "Kelola voucher",
    description: "Voucher produk & voucher ongkir: buat, ubah, aktif/nonaktifkan, riwayat pemakaian & audit log.",
  },
  {
    // Bundel produk (mig 8040) — preseden PERSIS `produk.voucher`: fitur baru,
    // jadi BUKAN grandfathered; diwarisi kunci modul "produk" (cermin baris
    // CASE `admin_permission_legacy_parent` mig 8040 §9). Guard server:
    // `admin_save_bundle` / `admin_set_bundle_status` / `admin_delete_bundle`.
    key: "produk.bundel", group: "produk", legacyParent: "produk", grandfathered: false,
    label: "Kelola bundling",
    description: "Bundling produk: buat, ubah, aktif/nonaktifkan, riwayat perubahan.",
  },
  {
    // Hadiah pembelian (mig 8680) — preseden PERSIS `produk.bundel`: fitur
    // baru, BUKAN grandfathered; diwarisi kunci modul "produk" (cermin baris
    // CASE `admin_permission_legacy_parent` mig 8680 §12). Guard server:
    // `admin_save_gift_rule` / `admin_set_gift_rule_status` / `admin_delete_gift_rule`.
    key: "produk.hadiah", group: "produk", legacyParent: "produk", grandfathered: false,
    label: "Kelola hadiah pembelian",
    description: "Aturan beli X gratis Y: buat, ubah, aktif/nonaktifkan, riwayat perubahan.",
  },

  // ── Pelanggan & Komunikasi ──
  {
    key: "pelanggan.kelola", group: "pelanggan", legacyParent: "pelanggan", grandfathered: true,
    label: "Kelola pelanggan",
    description: "Daftar pelanggan: approve pendaftaran, edit profil & alamat, nonaktif/hapus, ganti level per pelanggan.",
  },
  {
    key: "pelanggan.level", group: "pelanggan", legacyParent: "pelanggan", grandfathered: true,
    label: "Grup & level pelanggan",
    description: "Halaman Grup Pelanggan + aturan auto naik level.",
  },
  {
    key: "pelanggan.impersonate", group: "pelanggan", legacyParent: null, grandfathered: true, danger: true,
    label: "Login sebagai pelanggan",
    description: "Masuk ke akun pelanggan lewat tautan sekali pakai (impersonate) — dari halaman edit pelanggan maupun dari keranjang pesanannya.",
  },
  {
    key: "pelanggan.broadcast", group: "pelanggan", legacyParent: "pelanggan", grandfathered: true,
    label: "Broadcast & popup",
    description: "Kirim broadcast notifikasi/WhatsApp dan kelola popup toko.",
  },
  {
    // `grandfathered: false` — dan itu BUKAN kelalaian: bendera itu berarti
    // "mig 3780 mem-backfill kunci ini ke semua admin existing", dan kunci yang
    // baru lahir di mig 4540 tak pernah ikut backfill itu. Admin lama tetap
    // dapat aksesnya lewat PAYUNG `pelanggan` (cabang legacy di
    // admin_has_permission), jadi tak ada yang kehilangan apa pun.
    key: "pelanggan.kritik-saran", group: "pelanggan", legacyParent: "pelanggan", grandfathered: false,
    label: "Kotak kritik & saran",
    description: "Baca masukan pembeli, balas, tandai selesai & hapus permanen.",
  },

  // ── Kasir (POS) ──
  {
    key: "kasir.akses", group: "kasir", legacyParent: "pesanan", grandfathered: true,
    label: "Akses kasir (POS)",
    description: "Halaman kasir + riwayat transaksi kasir.",
  },
  {
    key: "kasir.void", group: "kasir", legacyParent: "pesanan", grandfathered: true, danger: true,
    label: "Batalkan transaksi kasir",
    description: "Void transaksi kasir (stok kembali + refund saldo otomatis).",
  },
  {
    // Hadiah Tahap 6-3 (`kasir_ubah_harga`, RENCANA §18.4) — kembaran
    // `pesanan.ubah-harga` untuk Kasir. Pola `pesanan.edit-produksi`:
    //  • `legacyParent: null` — tak diwarisi kunci modul `pesanan` (payung
    //    `kasir.akses`/`kasir.void`), tak ikut preset role → admin mana pun
    //    wajib dicentang eksplisit;
    //  • `grandfathered: false` TANPA backfill (H61): hari tayang hanya Super
    //    Admin (dan pemilik platform) yang memegangnya.
    // Guard server: `pos_assert_price` di `pos_checkout` (pembungkus 5 argumen
    // ikut) — tanpa kunci ini harga bersih baris katalog wajib di dalam rentang
    // katalog pembeli (`order_line_catalog_price_check`, gerbang yang sama
    // dengan `pesanan.ubah-harga`), diskon transaksi & item manual ditolak
    // (H62). Layar Kasir mengunci kolomnya (`useAdminPermission`).
    key: "kasir.ubah-harga", group: "kasir", legacyParent: null, grandfathered: false, danger: true,
    label: "Ubah harga & diskon di Kasir",
    description: "Ketik harga, diskon per barang, diskon transaksi, dan item manual di Kasir. Tanpa izin ini, harga Kasir dijaga di antara harga level pelanggan dan harga normal. Produk yang sedang promo hanya dijaga batas atasnya. Produk Non Web tidak dijaga.",
  },

  // ── Kasir Marketplace (mig 8550) ──
  // Dua kunci SENSITIF (legacyParent null → cabang `else null` SQL, tanpa baris
  // CASE): pemilik mencentangnya per admin. Tidak grandfathered — fiturnya baru.
  {
    key: "marketplace.catat", group: "marketplace", legacyParent: null, grandfathered: false,
    label: "Catat pesanan marketplace",
    description: "Scan pesanan Shopee & TikTok Shop (stok ready langsung terpotong), Hapus Scan & Batal, dan isi Dana Diterima saat scan.",
  },
  {
    key: "marketplace.keuangan", group: "marketplace", legacyParent: null, grandfathered: false, danger: true,
    label: "Keuangan marketplace",
    description: "Lihat angka uang pesanan marketplace, tempel Dana Diterima banyak pesanan sekaligus (masuk Laba Rugi), dan atur Toko Marketplace.",
  },
  // Integrasi Shopee Open Platform (mig 8930, docs/integrasi-shopee/DESAIN.md
  // §C9). SENSITIF: memegang Partner Key toko & bisa menimpa stok di Shopee.
  // Tak memberi akses angka uang (`marketplace.keuangan` tetap terpisah).
  {
    key: "marketplace.integrasi", group: "marketplace", legacyParent: null, grandfathered: false, danger: true,
    label: "Hubungkan & atur sinkron marketplace",
    description: "Hubungkan toko Shopee ke website, pasangkan produk Shopee ke varian ready stock, dan atur sinkron stok & pesanan.",
  },

  // ── Keuangan & Laporan ──
  {
    // Kunci ini juga membuka kartu uang Dashboard (`can_see_money` di
    // `admin_dashboard_today`, mig 4310) — dan angka di sana SE-TOKO, sedangkan
    // `report_*` menyaring admin non-super ke pelanggan yang ia tangani /
    // kasirnya sendiri. Deskripsi menyebutnya supaya pemilik tak memberi izin
    // dari katalog yang salah (bughunt G8-12).
    key: "keuangan.laporan", group: "keuangan", legacyParent: "pesanan", grandfathered: true,
    label: "Laporan penjualan & operasional",
    description: "Halaman Laporan: penjualan, pesanan, produk, pelanggan, ekspedisi, poin, kasir, laba produk, promo (Laba Rugi/Neraca tetap khusus Super Admin). Juga membuka kartu uang di Dashboard dengan angka seluruh toko, bukan hanya pelanggan yang ditangani admin ini.",
  },
  {
    key: "keuangan.biaya-produksi", group: "keuangan", legacyParent: "keuangan", grandfathered: false,
    label: "Biaya produksi & HPP (modal)",
    description: "Blok Biaya Produksi di Detail Produksi & board, Laporan Biaya Produksi + export Excel-nya.",
  },
  {
    key: "keuangan.biaya-operasional", group: "keuangan", legacyParent: "keuangan", grandfathered: true,
    label: "Biaya operasional",
    description: "Halaman Biaya Operasional: catat biaya, biaya rutin, prive, setoran modal, aset tetap.",
  },
  // ── Saldo Bank / Kas: dua tingkat × dua cakupan (mig 8620) ──
  // Permintaan pemilik 2026-10-26: admin tertentu boleh LIHAT saja atau
  // sekalian KELOLA saldo bank, dan admin kasir boleh mengelola kas tunai saja
  // + mencocokkan dengan uang di laci (Hitung Kas Laci). Empat kunci sensitif
  // (legacyParent null, tanpa backfill — saat mig 8620 tak ada admin non-super
  // yang memegang `keuangan.saldo-bank`). Kunci atas mencakup kunci bawah
  // (`implies`), cermin SQL `admin_bank_scope(level)`:
  //   lihat  = saldo-bank | saldo-bank-lihat → 'all';  kas-tunai | kas-tunai-lihat → 'cash'
  //   kelola = saldo-bank → 'all';  kas-tunai → 'cash'
  // `keuangan.saldo-bank` = kunci LAMA (dicek 9 fungsi SQL + Tarik Dana
  // Marketplace) → artinya tetap "kelola semua rekening", hanya labelnya
  // dipertegas jadi "Kelola …".
  {
    key: "keuangan.saldo-bank-lihat", group: "keuangan", legacyParent: null, grandfathered: false, danger: true,
    label: "Lihat Saldo Bank / Kas",
    description: "Lihat saldo & riwayat semua rekening dan kas tunai, unduh CSV, Cek Konsistensi, dan Laporan Saldo Bank. Tanpa tombol pencatatan.",
    implies: ["keuangan.kas-tunai-lihat"],
  },
  {
    key: "keuangan.saldo-bank", group: "keuangan", legacyParent: null, grandfathered: false, danger: true, superOnlyBefore: true,
    label: "Kelola Saldo Bank / Kas",
    description: "Semua rekening & kas tunai: kas masuk/keluar, mutasi antar rekening, rekonsiliasi, Hitung Kas Laci, dan Tarik Dana Marketplace. Sudah termasuk izin lihat.",
    implies: ["keuangan.saldo-bank-lihat", "keuangan.kas-tunai", "keuangan.kas-tunai-lihat"],
  },
  {
    key: "keuangan.kas-tunai-lihat", group: "keuangan", legacyParent: null, grandfathered: false,
    label: "Lihat Kas Tunai",
    description: "Lihat saldo, riwayat, dan riwayat hitung kas Tunai / Kas Toko saja. Rekening bank lain tetap tertutup.",
  },
  {
    key: "keuangan.kas-tunai", group: "keuangan", legacyParent: null, grandfathered: false, danger: true,
    label: "Kelola Kas Tunai & Hitung Laci",
    description: "Tunai / Kas Toko saja: Hitung Kas Laci (selisih dengan uang di laci langsung dibukukan) dan kas masuk/keluar. Mutasi ke rekening bank tetap butuh Kelola Saldo Bank / Kas.",
    implies: ["keuangan.kas-tunai-lihat"],
  },
  {
    // Mig 8660 — permintaan pemilik 2026-10-26: kasir mencatat biaya yang
    // dibayar dari laci tanpa izin "Biaya operasional" yang membuka seluruh
    // menu Biaya (rutin, prive, modal, aset) dan semua rekening sebagai sumber
    // dana. Satu pintu: RPC `admin_record_cash_expense` (sumber dana terkunci
    // Tunai, tanpa ubah/hapus — putusan pemilik; foto nota opsional sejak mig
    // 8690, koreksi pemilik 2026-10-26). Biayanya
    // biaya operasional biasa: masuk Laba Rugi & daftar Biaya Operasional.
    // Mencakup Lihat Kas Tunai (tombolnya ada di buku Tunai) — cermin SQL
    // `admin_bank_scope('lihat')` v2.
    key: "keuangan.biaya-tunai", group: "keuangan", legacyParent: null, grandfathered: false, danger: true,
    label: "Catat biaya dari kas tunai",
    description: "Tombol Catat Biaya di buku Tunai: biaya yang dibayar dari laci langsung memotong saldo Tunai dan masuk Laba Rugi. Tidak bisa mengubah atau menghapus biaya, dan tidak membuka menu Biaya Operasional.",
    implies: ["keuangan.kas-tunai-lihat"],
  },

  // ── Pengaturan Toko ──
  {
    key: "pengaturan.website", group: "pengaturan", legacyParent: "pengaturan", grandfathered: true,
    label: "Pengaturan & tampilan website",
    description: "Pengaturan Website, Tampilan Website (tema/banner/susunan), dan editor Halaman Tutorial.",
  },
  {
    key: "pengaturan.pembayaran", group: "pengaturan", legacyParent: "pengaturan", grandfathered: true,
    label: "Pengaturan rekening pembayaran",
    description: "Rekening bank/QRIS/e-wallet tujuan pembayaran + Biaya QRIS.",
  },
  {
    key: "pengaturan.pengiriman", group: "pengaturan", legacyParent: "pengaturan", grandfathered: true,
    label: "Pengaturan ekspedisi & ongkir",
    description: "Daftar ekspedisi + kurir & cek ongkir Biteship.",
  },
  {
    key: "pengaturan.cetak", group: "pengaturan", legacyParent: "pengaturan", grandfathered: true,
    label: "Template cetak & penomoran",
    description: "Template barcode, template tagihan/struk, dan penomoran dokumen.",
  },
  {
    key: "pengaturan.notifikasi", group: "pengaturan", legacyParent: "pengaturan", grandfathered: true,
    label: "Template WhatsApp & notifikasi",
    description: "Ubah isi pesan tombol WhatsApp + setelan notifikasi admin.",
  },

  // ── Data & Sistem ──
  {
    key: "data.import-export", group: "data", legacyParent: "pengaturan", grandfathered: true, danger: true,
    label: "Import & Export Data",
    description: "Import/export Excel semua entitas (pelanggan, produk, orderan, saldo, dll).",
  },
  {
    key: "data.backup", group: "data", legacyParent: null, grandfathered: false, danger: true, superOnlyBefore: true,
    label: "Backup & Restore Toko",
    description: "Unduh berkas backup toko dan pulihkan (restore) data toko dari berkas.",
  },
  {
    key: "data.reset", group: "data", legacyParent: null, grandfathered: false, danger: true, superOnlyBefore: true,
    label: "Reset Data",
    description: "Hapus massal per-domain (transaksi, produk, pelanggan, dsb) untuk toko ini.",
  },

  // ── Manajemen Admin ──
  {
    key: "admin.kelola", group: "admin", legacyParent: "admin", grandfathered: true,
    label: "Manajemen admin & assignment",
    description: "Buka halaman Manajemen Admin: daftar admin + assignment handler reseller. Mengubah role & perizinan tetap khusus Super Admin.",
  },
];

export const ADMIN_GRANT_KEYS: AdminGrantKey[] = ADMIN_GRANTS.map((g) => g.key);

const GRANT_META = new Map<string, AdminGrantMeta>(ADMIN_GRANTS.map((g) => [g.key, g]));

/** Kemampuan yang HARI INI dipegang semua admin — di-backfill mig 3780. */
export const GRANDFATHERED_GRANT_KEYS: AdminGrantKey[] = ADMIN_GRANTS.filter(
  (g) => g.grandfathered
).map((g) => g.key);

/**
 * Fitur yang TETAP khusus Super Admin (tidak bisa dicentang) — ditampilkan di
 * UI Perizinan sebagai daftar informasi supaya katalognya jujur & lengkap.
 *
 * Dashboard admin SENGAJA tak ada di sini (bughunt G8-12): sejak mig 4310
 * halamannya terbuka untuk semua admin, dan kartu uangnya ikut izin
 * `keuangan.laporan` — deskripsi izin itulah yang menyebutnya.
 */
export const SUPER_ADMIN_ONLY_NOTES: { label: string; description: string }[] = [
  { label: "Laporan keuangan lengkap", description: "Laba Rugi, Neraca, dan Arus Kas di halaman Laporan." },
  { label: "Total transaksi & estimasi profit Detail PO", description: "Dua angka uang di kartu ringkasan Detail Pre-Order." },
  { label: "Ubah role & perizinan admin", description: "Menyimpan perubahan di tab ini + membuat Super Admin baru." },
  { label: "Reset nomor urut dokumen", description: "Tombol reset counter di Penomoran Dokumen." },
  { label: "Bersihkan foto produk arsip", description: "Tombol “Bersihkan Sekarang” di Pengaturan Website." },
];

// ─── Kunci modul lama (payung back-compat) ────────────────────────────────────

export type AdminPermissionMeta = {
  key: AdminLegacyPermissionKey;
  label: string;
  description: string;
};

// Urutan kanonik modul lama — dipakai menormalkan daftar izin tersimpan.
export const ADMIN_PERMISSIONS: AdminPermissionMeta[] = [
  { key: "pesanan",    label: "Pesanan & Produksi",   description: "Board pesanan, PO, produksi, pengiriman" },
  { key: "stok",       label: "Stok",                 description: "Kelola stok, opname & kartu stok" },
  { key: "produk",     label: "Katalog Produk",       description: "Produk, kategori, merek & harga" },
  { key: "pelanggan",  label: "Pelanggan",            description: "Daftar pelanggan, level & handler" },
  { key: "keuangan",   label: "Keuangan",             description: "Saldo, tagihan & verifikasi pembayaran" },
  { key: "admin",      label: "Manajemen Admin",      description: "Tambah admin, atur role & perijinan" },
  { key: "pengaturan", label: "Pengaturan & Data",    description: "Pengaturan aplikasi, import & export" },
];

export const ADMIN_LEGACY_PERMISSION_KEYS: AdminLegacyPermissionKey[] =
  ADMIN_PERMISSIONS.map((p) => p.key);

/** @deprecated Nama lama — berisi kunci MODUL saja; pakai ADMIN_GRANT_KEYS utk granular. */
export const ADMIN_PERMISSION_KEYS: AdminLegacyPermissionKey[] = ADMIN_LEGACY_PERMISSION_KEYS;

// Urutan kanonik SEMUA kunci (legacy dulu, lalu granular sesuai katalog).
const ALL_PERMISSION_KEYS: AdminPermissionKey[] = [
  ...ADMIN_LEGACY_PERMISSION_KEYS,
  ...ADMIN_GRANT_KEYS,
];

// ─── Role ─────────────────────────────────────────────────────────────────────

export type AdminRoleMeta = {
  id: AdminRoleId;
  label: string;
  description: string;
  badgeClass: string;
  /**
   * Preset izin bawaan role dalam kunci MODUL lama (sumber sejarah; ekspansi
   * granularnya lewat `roleDefaultPermissions()` — itulah yang DISIMPAN).
   */
  defaultModules: AdminLegacyPermissionKey[];
  // Role dengan izin terkunci penuh (tak bisa di-uncheck di UI).
  locked: boolean;
};

export const ADMIN_ROLES: AdminRoleMeta[] = [
  {
    id: "super_admin",
    label: "Super Admin",
    description: "Akses penuh ke seluruh fitur & pengaturan.",
    badgeClass: "bg-purple-100 text-purple-700",
    defaultModules: [...ADMIN_LEGACY_PERMISSION_KEYS],
    locked: true,
  },
  {
    id: "admin_biaya",
    label: "Admin Biaya",
    description: "Fokus keuangan: saldo, tagihan & pembayaran.",
    badgeClass: "bg-blue-100 text-blue-700",
    defaultModules: ["keuangan", "pesanan"],
    locked: false,
  },
  {
    id: "admin_umum",
    label: "Admin Umum",
    description: "Operasional: pesanan, stok, produk & pelanggan.",
    badgeClass: "bg-orange-100 text-orange-700",
    defaultModules: ["pesanan", "stok", "produk", "pelanggan"],
    locked: false,
  },
];

export const DEFAULT_ADMIN_ROLE: AdminRoleId = "admin_umum";

export function roleMeta(role: string | null | undefined): AdminRoleMeta {
  return (
    ADMIN_ROLES.find((r) => r.id === role) ??
    ADMIN_ROLES.find((r) => r.id === DEFAULT_ADMIN_ROLE)!
  );
}

export function roleLabel(role: string | null | undefined): string {
  return roleMeta(role).label;
}

// ─── Ekspansi payung legacy → granular ────────────────────────────────────────

/** Kunci payung modul lama utk sebuah kunci granular (null = sensitif). */
export function legacyParentOf(key: string): AdminLegacyPermissionKey | null {
  return GRANT_META.get(key)?.legacyParent ?? null;
}

/** Semua kunci granular yang dipayungi daftar kunci modul lama. */
export function expandLegacyPermissions(
  legacyKeys: readonly string[]
): AdminGrantKey[] {
  const set = new Set(legacyKeys);
  return ADMIN_GRANTS.filter(
    (g) => g.legacyParent !== null && set.has(g.legacyParent)
  ).map((g) => g.key);
}

/**
 * Izin bawaan sebuah role — GRANULAR (ekspansi `defaultModules`). Inilah yang
 * DISIMPAN ke kolom `admin_permissions` saat admin dibuat / role diganti.
 * Catatan sengaja: `pelanggan.impersonate` & kunci sensitif lain TIDAK ikut
 * preset role mana pun — pemilik mencentangnya per admin.
 */
export function roleDefaultPermissions(
  role: string | null | undefined
): AdminPermissionKey[] {
  const meta = roleMeta(role);
  if (meta.id === "super_admin") return [...ADMIN_GRANT_KEYS];
  return expandLegacyPermissions(meta.defaultModules);
}

// ─── Validasi & normalisasi ───────────────────────────────────────────────────

export function isLegacyPermission(key: string): key is AdminLegacyPermissionKey {
  return (ADMIN_LEGACY_PERMISSION_KEYS as string[]).includes(key);
}

export function isGrantKey(key: string): key is AdminGrantKey {
  return GRANT_META.has(key);
}

export function isValidPermission(key: string): key is AdminPermissionKey {
  return isLegacyPermission(key) || isGrantKey(key);
}

export function isValidRole(role: string): role is AdminRoleId {
  return ADMIN_ROLES.some((r) => r.id === role);
}

// Normalkan daftar izin: buang yang tak dikenal & duplikat, urutkan kanonik
// (kunci modul lama DIPERTAHANKAN — ia payung back-compat, bukan sampah).
export function sanitizePermissions(
  perms: readonly string[] | null | undefined
): AdminPermissionKey[] {
  if (!perms) return [];
  const seen = new Set<string>(perms);
  return ALL_PERMISSION_KEYS.filter((k) => seen.has(k));
}

/**
 * Izin granular EFEKTIF: super admin selalu semua; lainnya = kunci granular
 * tersimpan ∪ ekspansi kunci modul lama yang tersimpan ∪ kunci yang tercakup
 * (`implies`). Dipakai UI Perizinan untuk merender keadaan centang yang
 * sebenarnya berlaku.
 */
export function effectivePermissions(
  role: string | null | undefined,
  perms: readonly string[] | null | undefined
): AdminGrantKey[] {
  if (roleMeta(role).id === "super_admin") return [...ADMIN_GRANT_KEYS];
  const stored = sanitizePermissions(perms);
  const granular = new Set<AdminGrantKey>(stored.filter(isGrantKey));
  for (const g of expandLegacyPermissions(stored.filter(isLegacyPermission))) {
    granular.add(g);
  }
  for (const k of [...granular]) {
    for (const i of impliedGrants(k)) granular.add(i);
  }
  return ADMIN_GRANT_KEYS.filter((k) => granular.has(k));
}

/** Semua kunci yang tercakup sebuah kunci (transitif, tanpa dirinya). */
export function impliedGrants(key: AdminGrantKey): AdminGrantKey[] {
  const out = new Set<AdminGrantKey>();
  const stack = [...(GRANT_META.get(key)?.implies ?? [])];
  while (stack.length) {
    const k = stack.pop()!;
    if (k === key || out.has(k)) continue;
    out.add(k);
    stack.push(...(GRANT_META.get(k)?.implies ?? []));
  }
  return ADMIN_GRANT_KEYS.filter((k) => out.has(k));
}

/**
 * Centang / lepas satu kunci di lembar Perizinan dengan menjaga `implies`:
 * mencentang ikut mencentang semua yang tercakup; melepas ikut melepas semua
 * kunci yang MENCAKUP-nya (melepas "Lihat Kas Tunai" tak boleh menyisakan
 * "Kelola Kas Tunai" yang diam-diam tetap memberi akses lihat). Urut katalog.
 */
export function toggleGrantWithImplies(
  current: readonly AdminGrantKey[],
  key: AdminGrantKey
): AdminGrantKey[] {
  const next = new Set<AdminGrantKey>(current);
  if (next.has(key)) {
    next.delete(key);
    for (const k of ADMIN_GRANT_KEYS) {
      if (impliedGrants(k).includes(key)) next.delete(k);
    }
  } else {
    next.add(key);
    for (const k of impliedGrants(key)) next.add(k);
  }
  return ADMIN_GRANT_KEYS.filter((k) => next.has(k));
}

// ─── Saldo Bank / Kas: cakupan akses (mig 8620) ───────────────────────────────

/** 'all' = semua rekening + kas tunai; 'cash' = Tunai / Kas Toko saja. */
export type BankAccessScope = "all" | "cash" | null;

export type BankAccess = {
  /** Boleh melihat saldo & riwayat (cakupan). */
  view: BankAccessScope;
  /** Boleh mencatat: kas masuk/keluar, rekonsiliasi, Hitung Kas Laci (cakupan). */
  manage: BankAccessScope;
};

export const NO_BANK_ACCESS: BankAccess = { view: null, manage: null };

/**
 * Cakupan akses Saldo Bank / Kas. CERMIN SQL `admin_bank_scope(level)`
 * (mig 8620) — kunci atas mencakup kunci bawah tanpa perlu tersimpan.
 * Mutasi antar rekening & Tarik Dana Marketplace tetap butuh `manage === "all"`.
 */
export function bankAccess(
  role: string | null | undefined,
  perms: readonly string[] | null | undefined
): BankAccess {
  const has = (k: AdminGrantKey) => hasPermission(role, perms, k);
  const manage: BankAccessScope = has("keuangan.saldo-bank")
    ? "all"
    : has("keuangan.kas-tunai")
      ? "cash"
      : null;
  const view: BankAccessScope =
    manage === "all" || has("keuangan.saldo-bank-lihat")
      ? "all"
      : manage === "cash" ||
          has("keuangan.kas-tunai-lihat") ||
          has("keuangan.biaya-tunai")
        ? "cash"
        : null;
  return { view, manage };
}

/**
 * Boleh mencatat biaya yang dibayar dari kas tunai (mig 8660)? Cermin guard
 * `admin_record_cash_expense`: izin khusus kasir ATAU izin Biaya operasional
 * (yang memang sudah boleh mencatat biaya dari sumber dana mana pun).
 */
export function canRecordCashExpense(
  role: string | null | undefined,
  perms: readonly string[] | null | undefined
): boolean {
  return (
    hasPermission(role, perms, "keuangan.biaya-tunai") ||
    hasPermission(role, perms, "keuangan.biaya-operasional")
  );
}

/** Cakupan `scope` mencakup rekening berjenis `kind`? */
export function bankScopeCovers(
  scope: BankAccessScope,
  kind: "account" | "cash"
): boolean {
  return scope === "all" || (scope === "cash" && kind === "cash");
}

/**
 * Cek satu izin. CERMIN SQL `admin_has_permission` (mig 0711 + 3780):
 * super admin → true; kunci ada di kolom → true; kunci granular yang payung
 * modul lamanya ada di kolom → true (back-compat data lama).
 */
export function hasPermission(
  role: string | null | undefined,
  perms: readonly string[] | null | undefined,
  key: AdminPermissionKey
): boolean {
  if (roleMeta(role).id === "super_admin") return true;
  if (!perms || perms.length === 0) return false;
  if (perms.includes(key)) return true;
  const parent = legacyParentOf(key);
  return parent !== null && perms.includes(parent);
}

export function grantMeta(key: string): AdminGrantMeta | undefined {
  return GRANT_META.get(key);
}

export function permissionLabel(key: string): string {
  return (
    GRANT_META.get(key)?.label ??
    ADMIN_PERMISSIONS.find((p) => p.key === key)?.label ??
    key
  );
}

/** Kunci granular satu kelompok, urut katalog. */
export function grantsOfGroup(group: AdminPermissionGroupId): AdminGrantMeta[] {
  return ADMIN_GRANTS.filter((g) => g.group === group);
}
